import { motion } from "framer-motion";
import { Img } from "@/components/Img";
import { Reveal, EASE } from "@/components/Motion";
import { CtaLink } from "@/components/CtaLink";

const THEMES = {
  light: { wrap: "bg-paper text-navy", muted: "text-slatey", num: "text-royal", line: "border-[var(--line)]", dot: "bg-royal", cta: "ghost" },
  navy: { wrap: "on-dark bg-navy text-white", muted: "text-white/80", num: "text-lime", line: "border-white/15", dot: "bg-lime", cta: "ghostDark" },
  mist: { wrap: "bg-mist text-navy", muted: "text-slatey", num: "text-royal", line: "border-navy/15", dot: "bg-royal", cta: "ghost" },
};

const ClipImage = ({ s, className }) => (
  <motion.div
    className={`overflow-hidden rounded-lg ${className}`}
    initial={{ clipPath: "inset(12% 12% 12% 12% round 12px)" }}
    whileInView={{ clipPath: "inset(0% 0% 0% 0% round 12px)" }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 1.3, ease: EASE }}
  >
    <Img name={s.image} alt={s.alt} sizes="(min-width:1024px) 50vw, 100vw" className="h-full w-full object-cover" />
  </motion.div>
);

const List = ({ items, t }) => (
  <ul className={`grid gap-x-8 sm:grid-cols-2 border-t ${t.line}`}>
    {items.map((it) => (
      <li key={it} className={`flex items-center gap-3 border-b py-3.5 text-[0.98rem] ${t.line}`}>
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${t.dot}`} aria-hidden="true" />{it}
      </li>
    ))}
  </ul>
);

const Heading = ({ s, t }) => (
  <>
    <p className={`font-mono text-sm ${t.num}`}>{s.n} — Service</p>
    <h2 className="mt-5 font-display text-4xl font-extrabold leading-[1.02] tracking-[-0.04em] sm:text-5xl lg:text-6xl">{s.title}</h2>
  </>
);

export const ServiceRow = ({ service: s }) => {
  const t = THEMES[s.theme];
  if (s.layout === "stacked") {
    return (
      <section id={s.slug} className={`scroll-mt-24 py-24 sm:py-32 ${t.wrap}`} aria-labelledby={`${s.slug}-title`} data-testid={`service-section-${s.slug}`}>
        <div className="shell">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
            <Reveal className="lg:col-span-7"><div id={`${s.slug}-title`}><Heading s={s} t={t} /></div></Reveal>
            <Reveal delay={0.1} className={`text-base leading-relaxed sm:text-lg lg:col-span-5 ${t.muted}`}><p>{s.intro}</p></Reveal>
          </div>
          <ClipImage s={s} className="mt-14 aspect-[4/3] sm:aspect-[21/9]" />
          <div className="mt-12 grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8"><List items={s.items} t={t} /></div>
            <div className="lg:col-span-4 lg:text-right"><CtaLink to="/contact" variant={t.cta} testid={`service-cta-${s.slug}`}>Discuss {s.title}</CtaLink></div>
          </div>
        </div>
      </section>
    );
  }
  return (
    <section id={s.slug} className={`scroll-mt-24 py-24 sm:py-32 ${t.wrap}`} aria-labelledby={`${s.slug}-title`} data-testid={`service-section-${s.slug}`}>
      <div className="shell grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
        <ClipImage s={s} className={`aspect-[4/5] lg:col-span-5 ${s.reverse ? "lg:order-1" : "lg:order-2 lg:col-start-8"}`} />
        <div className={`lg:col-span-6 ${s.reverse ? "lg:order-2 lg:col-start-7" : "lg:order-1"}`}>
          <Reveal><div id={`${s.slug}-title`}><Heading s={s} t={t} /></div></Reveal>
          <Reveal delay={0.1}><p className={`mt-6 max-w-xl text-base leading-relaxed sm:text-lg ${t.muted}`}>{s.intro}</p></Reveal>
          <Reveal delay={0.15} className="mt-10"><List items={s.items} t={t} /></Reveal>
          <Reveal delay={0.2} className="mt-10"><CtaLink to="/contact" variant={t.cta} testid={`service-cta-${s.slug}`}>Discuss this service</CtaLink></Reveal>
        </div>
      </div>
    </section>
  );
};
